import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Stack, FormControl, FormLabel, FormErrorMessage, Input, Select, Button, Text, Divider, useToast } from "@chakra-ui/react";
import { PageHeader, SectionCard } from "../../../shared/ui";
import { useAuth } from "../../auth/hooks/useAuth";
import { perfilSchema } from "../schemas/perfilSchema";
import { useUpdatePerfilMutation } from "../hooks/useUpdatePerfilMutation";
import { useGoogleCalendarStatusQuery } from "../hooks/useGoogleCalendarStatusQuery";
import { useConnectGoogleCalendarMutation } from "../hooks/useConnectGoogleCalendarMutation";
import { useDisconnectGoogleCalendarMutation } from "../hooks/useDisconnectGoogleCalendarMutation";
import AgendaFeedSection from "../components/AgendaFeedSection";
import ShiftRemindersSection from "../components/ShiftRemindersSection";
import SubscriptionSection from "../../assinatura/components/SubscriptionSection";

const OPCOES_REGIME_TRIBUTARIO = [
  { value: "simples_nacional", label: "Simples Nacional" },
  { value: "lucro_presumido", label: "Lucro Presumido" },
  { value: "lucro_real", label: "Lucro Real" },
];

export default function PerfilPage() {
  const toast = useToast();
  const { perfil } = useAuth();
  const mutation = useUpdatePerfilMutation();
  const googleCalendarQuery = useGoogleCalendarStatusQuery();
  const connectGoogleCalendar = useConnectGoogleCalendarMutation();
  const disconnectGoogleCalendar = useDisconnectGoogleCalendarMutation();
  const [searchParams, setSearchParams] = useSearchParams();
  const googleCalendarToastShown = useRef(false);
  const assinaturaRef = useRef(null);
  const [destacarAssinatura, setDestacarAssinatura] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({ resolver: zodResolver(perfilSchema) });

  // O perfil chega de forma assíncrona via useAuth() — sincroniza o formulário assim que
  // (ou sempre que) os dados mudarem, mesmo padrão usado em EditShiftModal.
  useEffect(() => {
    if (!perfil) return;
    reset({
      nomeCompleto: perfil.nome_completo ?? "",
      especialidade: perfil.especialidade ?? "",
      crm: perfil.crm ?? "",
      razaoSocialPj: perfil.razao_social_pj ?? "",
      cnpjPj: perfil.cnpj_pj ?? "",
      emailNotificacao: perfil.email_notificacao ?? "",
      regimeTributario: perfil.regime_tributario ?? "",
    });
  }, [perfil, reset]);

  // Depois do redirect de volta do consentimento do Google (googlecalendar/http.Callback
  // manda de volta para /perfil?google_calendar=connected|error), mostra um toast uma
  // única vez e limpa o query param — evita repetir o toast se a página recarregar.
  useEffect(() => {
    const resultado = searchParams.get("google_calendar");
    if (!resultado || googleCalendarToastShown.current) return;
    googleCalendarToastShown.current = true;

    if (resultado === "connected") {
      toast({ title: "Google Agenda conectado", status: "success", duration: 4000 });
    } else if (resultado === "error") {
      toast({
        title: "Não foi possível conectar ao Google Agenda",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }

    const proximosParams = new URLSearchParams(searchParams);
    proximosParams.delete("google_calendar");
    setSearchParams(proximosParams, { replace: true });
  }, [searchParams, setSearchParams, toast]);

  // O e-mail de fim de trial (cmd/reminders trial) aponta para /perfil?assinar=1: desce até
  // a seção Assinatura e destaca o botão. Não abre o checkout sozinho — ele cria cliente e
  // assinatura no Asaas, e um link antigo ou aberto por engano não deve fazer isso.
  useEffect(() => {
    if (searchParams.get("assinar") !== "1") return;
    setDestacarAssinatura(true);
    assinaturaRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });

    const proximosParams = new URLSearchParams(searchParams);
    proximosParams.delete("assinar");
    setSearchParams(proximosParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleConnectGoogleCalendar = () => {
    connectGoogleCalendar.mutate(undefined, {
      onError: () =>
        toast({
          title: "Não foi possível iniciar a conexão com o Google Agenda",
          status: "error",
          duration: 5000,
          isClosable: true,
        }),
    });
  };

  const handleDisconnectGoogleCalendar = () => {
    disconnectGoogleCalendar.mutate(undefined, {
      onSuccess: () => toast({ title: "Google Agenda desconectado", status: "info", duration: 4000 }),
      onError: (error) =>
        toast({
          title: "Não foi possível desconectar",
          description: error.response?.data?.mensagem,
          status: "error",
          duration: 5000,
          isClosable: true,
        }),
    });
  };

  const onSubmit = (values) => {
    mutation.mutate(values, {
      onSuccess: () => toast({ title: "Perfil atualizado", status: "success", duration: 4000 }),
      onError: (error) =>
        toast({
          title: "Não foi possível atualizar o perfil",
          description: error.response?.data?.mensagem,
          status: "error",
          duration: 5000,
          isClosable: true,
        }),
    });
  };

  return (
    <>
      <PageHeader title="Meu perfil" description="Dados usados na comprovação de plantões e no seu painel financeiro." />

      <SectionCard maxW="640px">
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={4}>
            <FormControl isInvalid={Boolean(errors.nomeCompleto)}>
              <FormLabel>Nome completo</FormLabel>
              <Input {...register("nomeCompleto")} />
              <FormErrorMessage>{errors.nomeCompleto?.message}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={Boolean(errors.especialidade)}>
              <FormLabel>Especialidade</FormLabel>
              <Input {...register("especialidade")} />
              <FormErrorMessage>{errors.especialidade?.message}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={Boolean(errors.crm)}>
              <FormLabel>CRM</FormLabel>
              <Input {...register("crm")} />
              <FormErrorMessage>{errors.crm?.message}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={Boolean(errors.razaoSocialPj)}>
              <FormLabel>Razão Social PJ</FormLabel>
              <Input {...register("razaoSocialPj")} />
              <FormErrorMessage>{errors.razaoSocialPj?.message}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={Boolean(errors.cnpjPj)}>
              <FormLabel>CNPJ PJ (ou CPF)</FormLabel>
              <Input placeholder="00.000.000/0001-00" {...register("cnpjPj")} />
              <FormErrorMessage>{errors.cnpjPj?.message}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={Boolean(errors.emailNotificacao)}>
              <FormLabel>E-mail de notificação</FormLabel>
              <Input placeholder="rh@hospital.com" {...register("emailNotificacao")} />
              <FormErrorMessage>{errors.emailNotificacao?.message}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={Boolean(errors.regimeTributario)}>
              <FormLabel>Regime tributário</FormLabel>
              <Select placeholder="Selecione" {...register("regimeTributario")}>
                {OPCOES_REGIME_TRIBUTARIO.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
              <FormErrorMessage>{errors.regimeTributario?.message}</FormErrorMessage>
            </FormControl>

            <Button type="submit" alignSelf="flex-start" isLoading={mutation.isPending}>
              Salvar
            </Button>
          </Stack>
        </form>
      </SectionCard>

      <Box ref={assinaturaRef} maxW="640px" mt={6}>
        <SectionCard
          title="Assinatura"
          {...(destacarAssinatura ? { borderColor: "primary", borderWidth: "2px" } : {})}
        >
          <SubscriptionSection origin={destacarAssinatura ? "email_fim_trial" : "perfil"} />
        </SectionCard>
      </Box>

      <SectionCard title="Lembretes de plantão" maxW="640px" mt={6}>
        <ShiftRemindersSection />
      </SectionCard>

      <SectionCard title="Integrações" maxW="640px" mt={6}>
        <Stack spacing={5}>
          <Stack spacing={3}>
            <Stack spacing={1}>
              <Text fontWeight="semibold" fontSize="sm">
                Google Agenda
              </Text>
              <Text fontSize="sm" color="app-muted-fg">
                {googleCalendarQuery.data?.connected
                  ? "Conectado: plantões registrados, reagendados ou cancelados aparecem na sua agenda na hora."
                  : "Conecte sua conta Google para que cada plantão apareça na sua agenda na hora — inclusive os que já estão agendados."}
              </Text>
            </Stack>
            {googleCalendarQuery.data?.connected ? (
              <Button
                variant="outline"
                size="sm"
                alignSelf="flex-start"
                isLoading={disconnectGoogleCalendar.isPending}
                onClick={handleDisconnectGoogleCalendar}
              >
                Desconectar
              </Button>
            ) : (
              <Button
                size="sm"
                alignSelf="flex-start"
                isLoading={googleCalendarQuery.isLoading || connectGoogleCalendar.isPending}
                onClick={handleConnectGoogleCalendar}
              >
                Conectar Google Agenda
              </Button>
            )}
          </Stack>

          <Divider />

          <AgendaFeedSection />
        </Stack>
      </SectionCard>
    </>
  );
}
